import { Box, Paper, Typography, useTheme } from '@mui/material';
import { DELIVERY_MAX_WIDTH, getDeliveryAddressTitle } from 'components/delivery/helpers';
import { useLocalHistory } from 'lib/useLocalHistory';
import { ROUTES } from 'pages/routes';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlatAppbar } from 'ui/FlatAppbar';
import { FlatToolbar } from 'ui/FlatToolbar';
import { isNumber } from 'lib/typeInference';
import { useDeliveryScenarioGuard } from 'components/delivery/hooks/useDeliveryScenarioGuard';
import { locationApi } from 'components/location/locationApi';
import logger from 'lib/logger';
import { Location } from 'components/location/model/Location';
import { ButtonV2 } from 'ui/ButtonV2';
import { ERouteScenario } from 'src/enums';
import { LoadingTypography } from 'lib/LoadingTypography';
import { useDeliveryState } from 'components/delivery/hooks/useDeliveryState';
import { setDeliveryTime, setBasketLocationId } from 'store/basket/basketSlice';
import { useDispatch } from 'react-redux';
import { useCurrencyString } from 'lib/useCurrencyString';
import { ErrorCircle } from 'ui/ErrorCircle';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';

export const DeliveryLocationsPage: React.FC = () => {
    useDeliveryScenarioGuard(ROUTES.DELIVERY.LOCATIONS);
    const { getParsedPath, push } = useLocalHistory();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const getCurrencyString = useCurrencyString();
    const { logUserEvent } = useGAHelpers();

    const [locationsLoading, setLocationsLoading] = React.useState(true);

    const [locations, setLocations] = React.useState<Location[]>([]);

    const { selectedAddress } = useDeliveryState();

    const handleLocationClick = React.useCallback(
        (locationId: string) => () => {
            const location = locations.find(item => item._id === locationId);
            if (!location) {
                return;
            }
            dispatch(setBasketLocationId(location._id));
            dispatch(setDeliveryTime(location.durationInMins));
            push(
                getParsedPath(ROUTES.MENU, {
                    locationId,
                    scenario: ERouteScenario.DELIVERY
                })
            );
        },
        [dispatch, getParsedPath, locations, push]
    );

    const renderLocation = React.useCallback(
        (location: Location) => (
            <Paper
                key={location._id}
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'primary.main', mb: 2 }}
            >
                <Box p={1}>
                    <Typography variant="h6" align="center">
                        {location.title}
                    </Typography>
                    <Typography align="center" sx={{ mb: 2, opacity: 0.75, fontSize: '0.9rem' }}>
                        {location.address.fullAddress}
                    </Typography>
                    {isNumber(location.durationInMins) && (
                        <Typography variant="body1" align="center">
                            {t('DELIVERY_ETA_MESSAGE', {
                                time: `${location.durationInMins} minute${
                                    location.durationInMins === 1 ? '' : 's'
                                }`
                            })}
                        </Typography>
                    )}
                    {isNumber(location.deliveryFee) && (
                        <Typography variant="body1" align="center">
                            {t('DELIVERY_COST', { price: getCurrencyString(location.deliveryFee) })}
                        </Typography>
                    )}
                    <ButtonV2 onClick={handleLocationClick(location._id)}>
                        {t('DELIVERY_LOCATION_CONFIRM_TITLE')}
                    </ButtonV2>
                </Box>
            </Paper>
        ),
        [getCurrencyString, handleLocationClick, t]
    );

    const renderSkeletonLocation = React.useCallback(
        (key: string) => (
            <Paper key={key} elevation={0} sx={{ border: '1px solid', borderColor: 'primary.main', mb: 2 }}>
                <Box p={1}>
                    <LoadingTypography sx={{ fontSize: '2rem', width: '65%', mx: 'auto' }} loading />
                    <LoadingTypography
                        sx={{ mb: 2, opacity: 0.75, fontSize: '1.5rem', width: '85%', mx: 'auto' }}
                        loading
                    />
                    <LoadingTypography variant="body1" sx={{ width: '50%', mx: 'auto' }} loading />
                    <ButtonV2 disabled>{t('DELIVERY_LOCATION_CONFIRM_TITLE')}</ButtonV2>
                </Box>
            </Paper>
        ),
        [t]
    );

    React.useEffect(() => {
        const loadLocations = async (long: number, lat: number) => {
            try {
                setLocationsLoading(true);
                const result = await locationApi.getDeliveryLocations(lat, long);
                setLocations(result.items);
            } catch (e) {
                // FIXME: add error handling
                logger.error(e);
            } finally {
                setLocationsLoading(false);
            }
        };
        if (selectedAddress) {
            const [long, lat] = selectedAddress.coordinates;
            loadLocations(long, lat);
        }
    }, [selectedAddress]);
    React.useEffect(() => {
        if (!locationsLoading && locations.length > 0) {
            logUserEvent(EGAEventName.DeliveryNoLocationsErrorShown);
        }
    }, [locations.length, locationsLoading, logUserEvent]);
    const theme = useTheme();
    return (
        <Box
            height="100%"
            width="100%"
            className="hidden-scroll"
            marginX="auto"
            maxWidth={DELIVERY_MAX_WIDTH}
            overflow="scroll"
            position="relative"
        >
            <Box sx={{ width: '100%', backgroundColor: 'background.default' }}>
                <FlatAppbar variant="dense">
                    <FlatToolbar
                        title={
                            !locationsLoading && locations.length === 0
                                ? t('DIALOG_OOPS')
                                : t('LOCATIONS_TITLE')
                        }
                        disableEdit
                    />
                </FlatAppbar>
                {locationsLoading && (
                    <Box p={2}>
                        <LoadingTypography
                            align="center"
                            sx={{ mb: 1.5, fontSize: '1.8rem', mx: 'auto', width: '80%' }}
                            loading
                        />
                        {renderSkeletonLocation('location-1')}
                        {renderSkeletonLocation('location-2')}
                    </Box>
                )}
                {!locationsLoading && locations.length > 0 && (
                    <Box p={2}>
                        <Typography align="center" sx={{ mb: 2, fontWeight: 700 }}>
                            {getDeliveryAddressTitle(selectedAddress)}
                        </Typography>
                        <Typography align="center" sx={{ mb: 2 }}>
                            {t('DELIVERY_LOCATIONS_TITLE')}
                        </Typography>
                        {locations.map(renderLocation)}
                    </Box>
                )}
                {!locationsLoading && locations.length === 0 && (
                    <Box
                        width="100%"
                        height="100%"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="flex-start"
                        pt={4}
                    >
                        <React.Fragment>
                            <ErrorCircle color={theme.palette.primary.main} />
                            <Typography
                                color="primary"
                                align="center"
                                sx={{ fontSize: '1.2rem', p: 2, pt: 4 }}
                            >
                                {t('DELIVERY_NO_LOCATIONS_MESSAGE')}
                            </Typography>
                        </React.Fragment>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
